// components/TokenExpirationWarning.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  setupTokenExpirationTimers,
  clearAuthData,
  logout,
} from '../../../services/authService';

/**
 * Component that handles token expiration warning and auto-logout.
 * Place this component once in your app (e.g., in Dashboard).
 */
export default function TokenExpirationWarning() {
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [cleanup, setCleanup] = useState(null);

  // Handler: Show warning 5 minutes before expiration
  const handleWarning = useCallback((timeRemaining) => {
    setWarningMessage(
      `Tu sesión expirará en ${timeRemaining}. ¿Deseas continuar trabajando?`
    );
    setWarningOpen(true);
  }, []);

  // Handler: Token expired - force logout
  const handleExpired = useCallback(() => {
    setWarningOpen(false);
    clearAuthData();
    
    // Show snackbar notification
    window.dispatchEvent(
      new CustomEvent('auth:showMessage', {
        detail: {
          message: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
          severity: 'warning',
        },
      })
    );
    
    // Redirect to login by dispatching logout event
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }, []);

  // Setup timers on mount
  useEffect(() => {
    const cleanupFn = setupTokenExpirationTimers(handleWarning, handleExpired);
    setCleanup(() => cleanupFn);

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [handleWarning, handleExpired]);

  // Listen for logout events from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' && !e.newValue) {
        // Token was removed in another tab
        clearAuthData();
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle "Continue working" - reset the timer
  const handleContinue = () => {
    setWarningOpen(false);
    
    // Clear existing timers
    if (cleanup) cleanup();
    
    // Reset the token creation time to extend the session
    // Note: This only extends the client-side timer. The server-side JWT will still expire.
    localStorage.setItem('authTokenCreatedAt', Date.now().toString());
    localStorage.removeItem('authWarningShown');
    
    // Setup new timers
    const newCleanup = setupTokenExpirationTimers(handleWarning, handleExpired);
    setCleanup(() => newCleanup);
  };

  // Handle "Logout now"
  const handleLogout = () => {
    setWarningOpen(false);
    logout();
  };

  return (
    <>
      {/* Warning Dialog */}
      <Dialog
        open={warningOpen}
        onClose={handleContinue}
        aria-labelledby="session-warning-title"
        aria-describedby="session-warning-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="session-warning-title">
          ⚠️ Sesión por expirar
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="session-warning-description">
            {warningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} color="inherit">
            Cerrar sesión
          </Button>
          <Button onClick={handleContinue} color="primary" variant="contained" autoFocus>
            Continuar trabajando
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
